import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@proaxiv/ui'
import { Button } from './Button'
import { HueSlider, SaturationValuePad } from './ColorGradientPicker'
import { hexToHsv, hexToRgb, hsvToHex, normalizeHex, rgbToHex } from './colorUtils'

/** Fixed swatch palette offered in every picker — deliberately small so it stays scannable. */
const PRESET_COLORS = [
  '#e53935',
  '#00e5ff',
  '#1e40ff',
  '#00c853',
  '#e91ec8',
  '#ffee00',
  '#000000',
  '#ffffff',
]

interface ColorPickerButtonProps {
  /** Current color as a 6-digit hex string (e.g. `"#1e40ff"`). */
  value: string
  /** Called with the newly chosen 6-digit hex color when the user confirms it. */
  onChange: (color: string) => void
  /** Human-readable name of the color slot being edited (e.g. "Accent") — used in the dialog title and swatch aria-label. */
  label: string
}

/**
 * Custom color picker trigger + dialog, replacing the bare `<input type="color">`.
 *
 * The native color input delegates to the OS/browser picker, which is neither translated
 * nor visually consistent between desktop and mobile (Android shows a bespoke "Select
 * color" sheet; iOS/desktop browsers each show something different again). This component
 * renders the same custom, fully-translated picker everywhere — a saturation/brightness
 * gradient pad + hue slider (matching the classic desktop color picker layout), a small
 * preset palette, and a hex text field — presented in the shared `Modal` overlay so it also
 * behaves consistently with every other dialog in the app (portal, backdrop, scroll lock).
 */
export function ColorPickerButton({ value, onChange, label }: ColorPickerButtonProps) {
  const { t } = useTranslation()
  const titleId = useId()
  const hexInputId = useId()
  const redInputId = useId()
  const greenInputId = useId()
  const blueInputId = useId()
  const [open, setOpen] = useState(false)
  const [hue, setHue] = useState(0)
  const [sat, setSat] = useState(0)
  const [val, setVal] = useState(0)
  const [hexText, setHexText] = useState(value)
  const [error, setError] = useState(false)

  // Re-seed every draft field from the committed value on open, so a previous cancelled edit
  // never leaks into the next open. Done in the click handler (not an effect) since it's a
  // direct response to a user action, not a sync with an external system.
  function openDialog() {
    const hsv = hexToHsv(value) ?? { h: 0, s: 0, v: 0 }
    setHue(hsv.h)
    setSat(hsv.s)
    setVal(hsv.v)
    setHexText(value)
    setError(false)
    setOpen(true)
  }

  /** Applies a new HSV triple from the gradient pad/hue slider/preset swatches, keeping the hex field in sync. */
  function applyHsv(nextHue: number, nextSat: number, nextVal: number) {
    setHue(nextHue)
    setSat(nextSat)
    setVal(nextVal)
    setHexText(hsvToHex({ h: nextHue, s: nextSat, v: nextVal }))
    setError(false)
  }

  function applyPreset(preset: string) {
    const hsv = hexToHsv(preset)
    if (!hsv) return
    applyHsv(hsv.h, hsv.s, hsv.v)
  }

  function handleHexTextChange(text: string) {
    setHexText(text)
    setError(false)
    const hsv = hexToHsv(text)
    if (hsv) {
      setHue(hsv.h)
      setSat(hsv.s)
      setVal(hsv.v)
    }
  }

  /** Applies an edited R/G/B channel (0-255, out-of-range values clamped by `rgbToHex`), keeping the hex and gradient/hue state in sync. */
  function handleRgbChannelChange(channel: 'r' | 'g' | 'b', text: string) {
    const parsed = Number(text)
    if (text.trim() === '' || Number.isNaN(parsed)) return
    const current = hexToRgb(currentHex ?? '#000000')
    const next = { ...current, [channel]: parsed }
    const hex = rgbToHex(next.r, next.g, next.b)
    setHexText(hex)
    setError(false)
    const hsv = hexToHsv(hex)
    if (hsv) {
      setHue(hsv.h)
      setSat(hsv.s)
      setVal(hsv.v)
    }
  }

  function commit() {
    const normalized = normalizeHex(hexText)
    if (!normalized) {
      setError(true)
      return
    }
    onChange(normalized)
    setOpen(false)
  }

  const currentHex = normalizeHex(hexText)
  const currentRgb = hexToRgb(currentHex ?? '#000000')
  const rgbFields: Array<{
    id: string
    channel: 'r' | 'g' | 'b'
    labelKey: string
  }> = [
    { id: redInputId, channel: 'r', labelKey: 'ep_color_picker_red_label' },
    { id: greenInputId, channel: 'g', labelKey: 'ep_color_picker_green_label' },
    { id: blueInputId, channel: 'b', labelKey: 'ep_color_picker_blue_label' },
  ]

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className="border-pax-purple/30 hover:border-pax-blue h-9 w-9 shrink-0 cursor-pointer rounded-lg border p-0.5 transition-colors"
        style={{ backgroundColor: value }}
        aria-label={label}
      />

      {open && (
        <Modal labelledBy={titleId} onBackdropClick={() => setOpen(false)}>
          <div className="border-pax-purple/50 relative w-full max-w-xs rounded-2xl border bg-white p-6 shadow-2xl ring-1 ring-black/5">
            <h2 id={titleId} className="text-pax-black mb-4 text-lg font-bold">
              {t('ep_color_picker_title', { label })}
            </h2>

            <div className="mb-4 flex flex-col gap-3">
              <SaturationValuePad
                hue={hue}
                saturation={sat}
                value={val}
                onChange={(s, v) => applyHsv(hue, s, v)}
                label={t('ep_color_picker_sv_label')}
              />
              <HueSlider
                hue={hue}
                onChange={(h) => applyHsv(h, sat, val)}
                label={t('ep_color_picker_hue_label')}
              />
            </div>

            <p className="text-pax-gray mb-2 text-xs font-medium tracking-wide uppercase">
              {t('ep_color_picker_rgb_label')}
            </p>
            <div className="mb-5 grid grid-cols-3 gap-2">
              {rgbFields.map(({ id, channel, labelKey }) => (
                <div key={channel}>
                  <label htmlFor={id} className="text-pax-black mb-1 block text-xs font-medium">
                    {t(labelKey)}
                  </label>
                  <input
                    id={id}
                    type="number"
                    min={0}
                    max={255}
                    step={1}
                    value={currentRgb[channel]}
                    onChange={(e) => handleRgbChannelChange(channel, e.target.value)}
                    className="border-pax-purple/30 text-pax-black focus:border-pax-blue w-full rounded-xl border bg-white px-2 py-2 text-sm transition-colors outline-none"
                  />
                </div>
              ))}
            </div>

            <p className="text-pax-gray mb-2 text-xs font-medium tracking-wide uppercase">
              {t('ep_color_picker_presets')}
            </p>
            <div className="mb-5 grid grid-cols-4 gap-2">
              {PRESET_COLORS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => applyPreset(preset)}
                  aria-label={preset}
                  aria-pressed={currentHex === preset}
                  className={`h-9 w-full cursor-pointer rounded-lg border transition-all ${
                    currentHex === preset
                      ? 'ring-pax-blue border-transparent ring-2 ring-offset-1'
                      : 'border-pax-purple/30 hover:scale-105'
                  }`}
                  style={{ backgroundColor: preset }}
                />
              ))}
            </div>

            <label htmlFor={hexInputId} className="text-pax-black mb-1 block text-xs font-medium">
              {t('ep_color_picker_custom_label')}
            </label>
            <div className="mb-1 flex items-center gap-2">
              <span
                className="border-pax-purple/30 h-9 w-9 shrink-0 rounded-lg border"
                style={{ backgroundColor: currentHex ?? undefined }}
                aria-hidden="true"
              />
              <input
                id={hexInputId}
                type="text"
                value={hexText}
                onChange={(e) => handleHexTextChange(e.target.value)}
                placeholder="#1A2B3C"
                aria-invalid={error}
                className={`border-pax-purple/30 text-pax-black focus:border-pax-blue w-full rounded-xl border bg-white px-3 py-2 font-mono text-sm transition-colors outline-none ${
                  error ? 'border-pax-red!' : ''
                }`}
              />
            </div>
            {error && (
              <p className="text-pax-red mb-3 text-xs">{t('ep_color_picker_invalid_hex')}</p>
            )}

            <div className={`flex justify-end gap-3 ${error ? 'mt-3' : 'mt-6'}`}>
              <Button variant="secondary" onClick={() => setOpen(false)}>
                {t('btn_cancel')}
              </Button>
              <Button variant="primary" onClick={commit}>
                {t('btn_confirm')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
